import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment";

import { PICKING_STATE } from "@/components/DatePickers/shared/types";
import LanguageContext from "@/context/language/LanguageContext";

import { useBaseDayPicker } from "../../shared/hooks/useBaseDayPicker";
import { DayRangePickerProps } from "../types";

export const useDayRangePicker = ({
  minDate,
  maxDate,
  disableSameDay,
  disabled,
  externalValue,
  onChange
}: Pick<
  DayRangePickerProps,
  | "minDate"
  | "maxDate"
  | "disableSameDay"
  | "disabled"
  | "externalValue"
  | "onChange"
>) => {
  const { currentDateFormat } = useContext(LanguageContext);
  const { t } = useTranslation();

  const base = useBaseDayPicker({ minDate, maxDate, disabled });

  const [startDay, setStartDay] = useState<number | null>(null);
  const [endDay, setEndDay] = useState<number | null>(null);
  const [hoverDay, setHoverDay] = useState<number | null>(null);
  const [picking, setPicking] = useState<PICKING_STATE | null>(null);

  useEffect(() => {
    if (base.open) return;
    if (externalValue === undefined) return;

    if (!externalValue) {
      setStartDay(null);
      setEndDay(null);
      return;
    }

    const { start, end } = externalValue;
    const toDayKey = (year: number, month: number, day: number) =>
      new Date(year, month, day).getTime();

    setStartDay(
      toDayKey(start.getFullYear(), start.getMonth(), start.getDate())
    );
    setEndDay(toDayKey(end.getFullYear(), end.getMonth(), end.getDate()));
  }, [externalValue, base.open]);

  const noStartDay = startDay === null;
  const noEndDay = endDay === null;

  const openPicker = () => {
    base.openPicker(() => {
      setPicking(PICKING_STATE.Start);
      if (noStartDay && noEndDay) {
        base.setYear(base.currentYear);
        base.setMonth(base.currentMonth);
      }
    });
  };

  const closePicker = () => {
    base.closePicker();
    setPicking(null);
    setHoverDay(null);
  };

  const handleDayClick = (clickedKey: number) => {
    if (
      disableSameDay &&
      picking === PICKING_STATE.End &&
      clickedKey === startDay
    )
      return;

    if (picking === PICKING_STATE.Start) {
      setStartDay(clickedKey);
      setEndDay(null);
      setHoverDay(null);
      setPicking(PICKING_STATE.End);
      onChange?.(null);
      return;
    }

    if (picking === PICKING_STATE.End && startDay !== null) {
      if (clickedKey < startDay) {
        setStartDay(clickedKey);
        setEndDay(null);
        setHoverDay(null);
        setPicking(PICKING_STATE.End);
        onChange?.(null);
        return;
      }

      setEndDay(clickedKey);
      setHoverDay(null);
      setPicking(null);
      base.cancelHoverClear();
      onChange?.({
        start: moment(startDay).toDate(),
        end: moment(clickedKey).toDate()
      });
      closePicker();
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    if (disabled) return;
    event.stopPropagation();
    base.cancelHoverClear();
    onChange?.(null);
    setStartDay(null);
    setEndDay(null);
    setHoverDay(null);
    setPicking(PICKING_STATE.Start);
  };

  const getDisplayValue = (): string => {
    if (noStartDay) return t("Filter##Select date range");

    const start = Math.min(startDay, endDay ?? startDay);
    const end = noEndDay ? null : Math.max(startDay, endDay);

    return t("Filter##from to date", {
      fromDate: moment(start).format(currentDateFormat),
      toDate: end === null ? "" : moment(end).format(currentDateFormat)
    });
  };

  return {
    ...base,
    startDay,
    endDay,
    hoverDay,
    picking,
    noStartDay,
    noEndDay,
    hasClearableValue: startDay !== null || endDay !== null,
    setHoverDay,
    openPicker,
    closePicker,
    handleDayClick,
    handleClear,
    getDisplayValue
  };
};
